// Modal "Auto-preencher (Clockify)" da tela de profissionais.
//
// ORIGEM: extraído de pages/Profissionais.tsx (linhas ~796-966, em
// 2026-08-25) — aquela tela será aposentada quando a ficha do hub RH entrar
// no ar. A lógica é a mesma: ao abrir, roda o dry-run da action
// `profissional_auto_preencher` (comercial-engine) e pré-seleciona só quem
// tem mudança real; Aplicar confirma e roda `modo: 'aplicar'` com os ids
// escolhidos. `aoAplicar` avisa o pai para recarregar a lista.
import { ReactElement, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, Button, Checkbox, Flex, Modal, Spin, Table, Tag, Typography,
  App as AntApp,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

const { Text } = Typography;

function fmtSeg(seg: number) {
  if (!seg) return '0h';
  const h = Math.floor(seg / 3600);
  return `${h}h`;
}

function fmtMoney(v: number | null) {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function AutoPreencherClockify({ aberto, onFechar, aoAplicar }: {
  aberto: boolean;
  onFechar: () => void;
  /** Chamado depois de aplicar com sucesso — o pai recarrega a lista. */
  aoAplicar: () => void;
}): ReactElement {
  const { message, modal } = AntApp.useApp();
  const [carregando, setCarregando] = useState(false);
  const [propostas, setPropostas] = useState<any[]>([]);
  const [idsAplicar, setIdsAplicar] = useState<Set<string>>(new Set());
  const [forcar, setForcar] = useState(false);
  const [aplicando, setAplicando] = useState(false);

  // Dry-run a cada abertura do modal.
  useEffect(() => {
    if (!aberto) return;
    let vivo = true;
    (async () => {
      setCarregando(true);
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'profissional_auto_preencher', modo: 'dry-run' },
          { headers: { Authorization: token }, timeout: 60_000 });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        const props = Array.isArray(d?.propostas) ? d.propostas : [];
        if (!vivo) return;
        setPropostas(props);
        // Por default, seleciona só quem tem algo proposto E sem valor manual
        // (i.e., onde o auto-preencher VAI alterar algo)
        const ids = new Set<string>();
        for (const p of props) {
          const temMudanca =
            (p.salario_esperado_atual === null && p.salario_esperado_proposto !== null) ||
            (p.clockify_user_id_atual === null && p.clockify_user_id_proposto !== null) ||
            (!p.usa_clockify_atual && p.usa_clockify_proposto);
          if (temMudanca) ids.add(p.profissional_id);
        }
        setIdsAplicar(ids);
      } catch (e: any) {
        if (vivo) message.error('Erro: ' + (e.response?.data?.error || e.message));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, [aberto, message]);

  async function aplicar() {
    if (idsAplicar.size === 0) { message.warning('Nenhum profissional selecionado'); return; }
    setAplicando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'profissional_auto_preencher', modo: 'aplicar',
          forcar,
          ids_aplicar: Array.from(idsAplicar) },
        { headers: { Authorization: token }, timeout: 60_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success(`${d.aplicados} profissional(is) atualizado(s)`);
      onFechar();
      aoAplicar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setAplicando(false); }
  }

  return (
    <Modal
      open={aberto} onCancel={onFechar}
      title="🪄 Auto-preencher dados Clockify dos profissionais"
      // Regra da casa: modal com tabela usa a tela que houver.
      width="94vw" style={{ maxWidth: 1000, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      footer={[
        <Checkbox key="f" checked={forcar} onChange={(e) => setForcar(e.target.checked)}
          style={{ marginRight: 'auto' }}>
          Forçar (sobrescrever valores manuais)
        </Checkbox>,
        <Button key="c" onClick={onFechar}>Cancelar</Button>,
        <Button key="a" type="primary" loading={aplicando}
          disabled={idsAplicar.size === 0}
          onClick={() => modal.confirm({
            title: `Aplicar em ${idsAplicar.size} profissional(is)?`,
            content: forcar
              ? 'ATENÇÃO: a opção "Forçar" está marcada — vai SOBRESCREVER valores que você inseriu manualmente.'
              : 'Vai preencher apenas onde está vazio. Valores manuais existentes são preservados.',
            okText: 'Aplicar', cancelText: 'Voltar',
            onOk: aplicar,
          })}>
          Aplicar ({idsAplicar.size})
        </Button>,
      ]}
    >
      <Alert type="info" showIcon style={{ marginBottom: 12, fontSize: 12 }}
        message="Como funciona (modelo Coef-Extras)"
        description={
          <div>
            <div>• <b>Match prof</b>: via nome canônico + aliases na tabela profissionais_aliases</div>
            <div>• <b>Classificação de lançamentos</b> dos últimos 12 meses por descrição:
              <Tag color="blue" style={{ marginLeft: 4 }}>honorário</Tag>
              <Tag color="orange">férias</Tag>
              <Tag color="purple">bônus</Tag>
              <Tag color="cyan">13º</Tag>
              <Tag color="default">excluir: comissão, dividendos, reembolsos, CAPEX</Tag>
            </div>
            <div>• <b>Salário esperado</b>: mediana dos meses com honorário base (informativo)</div>
            <div>• <b>Coeficiente</b>: (honorário + extras) / honorário — captura adicionais anuais</div>
            <div>• <b>Ativar rateio</b>: tem user Clockify <i>e</i> ≥ 20h em 3 meses</div>
            <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
              No rateio mensal: o sistema pega tudo classificado como remuneração do mês e distribui pelas horas Clockify.
              Bônus/férias caem nos CCs onde o prof trabalhou MAIS naquele mês.
            </div>
          </div>
        } />

      {carregando ? (
        <Flex justify="center" style={{ padding: 40 }}><Spin /></Flex>
      ) : (
        <Table size="small" rowKey="profissional_id" dataSource={propostas}
          pagination={{ pageSize: 20 }}
          rowSelection={{
            selectedRowKeys: Array.from(idsAplicar),
            onChange: (keys) => setIdsAplicar(new Set(keys as string[])),
          }}
          expandable={{
            expandedRowRender: (r: any) => (
              <div style={{ padding: '4px 12px', background: '#fafafa', fontSize: 11 }}>
                <Flex gap={16} wrap>
                  <div>📋 <b>Aliases</b>: {(r.aliases || []).join(', ') || '—'}</div>
                  <div>📅 <b>Meses c/ honorário</b>: {r.meses_com_honorario}</div>
                </Flex>
                <Flex gap={16} wrap style={{ marginTop: 6 }}>
                  <div>💰 <b>Honorário 12m</b>: {fmtMoney(r.total_honorario_12m)}</div>
                  <div>🏖️ <b>Férias 12m</b>: {fmtMoney(r.total_ferias_12m || 0)}</div>
                  <div>🎁 <b>Bônus 12m</b>: {fmtMoney(r.total_bonus_12m || 0)}</div>
                  <div>📆 <b>13º 12m</b>: {fmtMoney(r.total_decimo_12m || 0)}</div>
                  <div>🧾 <b>INSS 12m</b>: {fmtMoney(r.total_inss_12m || 0)}</div>
                </Flex>
                <Flex gap={8} wrap style={{ marginTop: 6, fontSize: 10, color: '#666' }}>
                  {Object.entries(r.tipos_breakdown || {}).map(([k, v]) => (
                    <span key={k}>{k}: {String(v)}</span>
                  ))}
                </Flex>
              </div>
            ),
          }}
          columns={[
            {
              title: 'Profissional', dataIndex: 'nome_canonical', key: 'n', fixed: 'left', width: 180,
              render: (v: string, r: any) => (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{v}</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{r.metodo}</div>
                </div>
              ),
            },
            {
              title: 'Usuário Clockify', key: 'cu', width: 220,
              render: (_: any, r: any) => {
                if (!r.clockify_user_id_proposto) return <Text type="secondary" style={{ fontSize: 11 }}>— não encontrado —</Text>;
                const mesmo = r.clockify_user_id_atual === r.clockify_user_id_proposto;
                return (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 500 }}>
                      {r.clockify_user_name || r.clockify_user_email}
                      {mesmo && <Tag color="default" style={{ marginLeft: 4, fontSize: 9 }}>já</Tag>}
                    </div>
                    <div style={{ fontSize: 10, color: '#888' }}>{r.clockify_user_email}</div>
                  </div>
                );
              },
            },
            {
              title: 'Horas 3m', dataIndex: 'horas_3meses_seg', key: 'h3', width: 80, align: 'right',
              render: (v: number) => fmtSeg(v),
            },
            {
              title: 'Honorário 12m', dataIndex: 'total_honorario_12m', key: 'th', width: 140, align: 'right',
              render: (v: number) => <strong>{fmtMoney(v)}</strong>,
            },
            {
              title: 'Extras 12m', dataIndex: 'total_extras_12m', key: 'te', width: 130, align: 'right',
              render: (v: number) => (
                <span style={{ color: v > 0 ? '#fa8c16' : '#999' }}>{fmtMoney(v)}</span>
              ),
            },
            {
              title: 'Salário mediano', key: 'se', width: 150, align: 'right',
              render: (_: any, r: any) => (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>
                    {fmtMoney(r.salario_esperado_proposto)}
                  </div>
                  {r.salario_esperado_atual !== null && r.salario_esperado_atual !== r.salario_esperado_proposto && (
                    <div style={{ fontSize: 10, color: '#888' }}>
                      atual: {fmtMoney(r.salario_esperado_atual)}
                    </div>
                  )}
                </div>
              ),
            },
            {
              title: 'Coef', key: 'cf', width: 90, align: 'center',
              render: (_: any, r: any) => (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>
                    {r.coeficiente_proposto !== null ? r.coeficiente_proposto.toFixed(4) : '—'}
                  </div>
                  {r.coeficiente_atual !== null && r.coeficiente_atual !== r.coeficiente_proposto && (
                    <div style={{ fontSize: 10, color: '#888' }}>
                      atual: {r.coeficiente_atual.toFixed(2)}
                    </div>
                  )}
                </div>
              ),
            },
            {
              title: 'Ativar rateio?', key: 'ur', width: 110, align: 'center',
              render: (_: any, r: any) => {
                const mesmo = r.usa_clockify_atual === r.usa_clockify_proposto;
                return (
                  <div>
                    {r.usa_clockify_proposto
                      ? <Tag color="success">✓ Sim</Tag>
                      : <Tag color="default">Não</Tag>}
                    {!mesmo && (
                      <div style={{ fontSize: 9, color: '#888', marginTop: 2 }}>
                        (atual: {r.usa_clockify_atual ? 'sim' : 'não'})
                      </div>
                    )}
                  </div>
                );
              },
            },
          ] as ColumnsType<any>}
          scroll={{ x: 1100 }}
        />
      )}
    </Modal>
  );
}
